import CustomSnackBar from "@components/CustomSnackBar";
import { ROUTING_ADMIN_CLIENT_MODELS } from "@constants/adminPanelRouting";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState
} from "@constants/alerts";
import { BUTTON_SECONDARY_ICON_FONT_SIZE } from "@constants/button";
import ROUTES from "@constants/routing";
import ScienceIcon from "@mui/icons-material/Science";
import {
  Box,
  Divider,
  Paper,
  Skeleton,
  Typography,
  useTheme
} from "@mui/material";
import {
  useClient,
  useClientModel,
  useClientModelInput,
  useClientModelOutput,
  useClientModelParams
} from "@services/api";
import NavigationButtons from "@views/ViewAdminPanel/components/NavigationButtons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsFiletypeCsv, BsFiletypeJson } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import ModelFileCSVViewer, {
  ModelFileCSVViewerSkeleton
} from "./components/ModelFileCSVViewer";
import ModelParamsViewer, {
  ModelParamsViewerSkeleton
} from "./components/ModelParamsViewer";

const ViewClientModel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });

  const { id: clientId, modelId } = useParams<{
    id: string;
    modelId: string;
  }>();

  const {
    data: client,
    isFetching: isLoadingClient,
    isError: isErrorClient
  } = useClient(clientId || "");

  const {
    data: model,
    isFetching: isLoadingModel,
    isError: isErrorModel
  } = useClientModel(modelId || "");

  const { data: modelInput, isFetching: isLoadingModelInput } =
    useClientModelInput(modelId || "");

  const { data: modelOutput, isFetching: isLoadingModelOutput } =
    useClientModelOutput(modelId || "");

  const { data: modelParams, isFetching: isLoadingModelParams } =
    useClientModelParams(modelId || "");

  const onGoBack = () => {
    navigate(`${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_CLIENT_MODELS}`, {
      state: { clientId }
    });
  };

  const clientName = useMemo(() => client?.name || "", [client]);
  const modelName = useMemo(() => model?.name || "", [model]);

  const isLoading = useMemo(() => {
    return (
      isLoadingClient ||
      isLoadingModel ||
      isLoadingModelInput ||
      isLoadingModelOutput ||
      isLoadingModelParams
    );
  }, [
    isLoadingClient,
    isLoadingModel,
    isLoadingModelInput,
    isLoadingModelOutput,
    isLoadingModelParams
  ]);

  useEffect(() => {
    if (!isLoadingClient && (isErrorClient || client === undefined)) {
      setSnackbarAlert(
        setErrorAlert(t("clientModel.errorClientNotFound", { id: clientId }))
      );
    } else if (!isLoadingModel && (isErrorModel || model === undefined)) {
      setSnackbarAlert(
        setErrorAlert(t("clientModel.errorModelNotFound", { id: modelId }))
      );
    }
  }, [isLoadingClient, isErrorClient, isLoadingModel, isErrorModel]);

  const isModelParamsLoading = useMemo(() => {
    return isLoadingModelParams || modelParams === undefined;
  }, [isLoadingModelParams, modelParams]);

  const isModelInputLoading = useMemo(() => {
    return isLoadingModelInput || modelInput === undefined;
  }, [isLoadingModelInput, modelInput]);

  const isModelOutputLoading = useMemo(() => {
    return isLoadingModelOutput || modelOutput === undefined;
  }, [isLoadingModelOutput, modelOutput]);

  return (
    <Box className="w-full p-4">
      <NavigationButtons disabled={false} onGoBack={onGoBack} />
      <Box className="flex justify-center w-full p-8 mt-8">
        <Paper className="w-full">
          <Box className="flex items-center justify-center pt-16 gap-2">
            <Box className="flex">
              <ScienceIcon color="success" sx={{ fontSize: "2.0rem" }} />
            </Box>
            {!isLoading && clientName.length > 0 ? (
              <Typography variant="h6">
                {clientName}: {modelName}
              </Typography>
            ) : (
              <Skeleton variant="text" width="10%" height="100%" />
            )}
          </Box>
          {!isLoadingModelParams && modelParams && (
            <Box className="w-full pt-8 pb-8 pl-16 pr-16">
              <Box className="flex items-center gap-2">
                <BsFiletypeJson
                  style={{
                    fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE,
                    color: theme.palette.info.main
                  }}
                />
                <Typography variant="h6">{modelParams.name}</Typography>
              </Box>
              <ModelParamsViewer data={modelParams.data} />
            </Box>
          )}
          {isModelParamsLoading && (
            <Box className="w-full p-8">
              <Typography variant="h6">
                {t("clientModel.loadingParamsFile", { name: modelName })}
              </Typography>
              <ModelParamsViewerSkeleton />
            </Box>
          )}
          <Divider />
          {!isLoadingModelInput && modelInput && (
            <Box className="w-full pt-8 pb-8 pl-16 pr-16">
              <Box className="flex items-center gap-2">
                <BsFiletypeCsv
                  style={{
                    fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE,
                    color: theme.palette.info.main
                  }}
                />
                <Typography variant="h6">{modelInput.name}</Typography>
              </Box>
              <ModelFileCSVViewer data={modelInput} />
            </Box>
          )}
          {isModelInputLoading && (
            <Box className="w-full p-8">
              <Typography variant="h6">
                {t("clientModel.loadingInputFile", { name: modelName })}
              </Typography>
              <ModelFileCSVViewerSkeleton />
            </Box>
          )}
          <Divider />
          {!isLoadingModelOutput && modelOutput && (
            <Box className="w-full pt-8 pb-8 pl-16 pr-16">
              <Box className="flex items-center gap-2">
                <BsFiletypeCsv
                  style={{
                    fontSize: BUTTON_SECONDARY_ICON_FONT_SIZE,
                    color: theme.palette.info.main
                  }}
                />
                <Typography variant="h6">{modelOutput.name}</Typography>
              </Box>
              <ModelFileCSVViewer data={modelOutput} />
            </Box>
          )}
          {isModelOutputLoading && (
            <Box className="w-full p-8">
              <Typography variant="h6">
                {t("clientModel.loadingOutputFile", { name: modelName })}
              </Typography>
              <ModelFileCSVViewerSkeleton />
            </Box>
          )}
          <Divider />
        </Paper>
        <CustomSnackBar
          id={`client-${clientId}-model-${modelId}-snack-bar`}
          open={snackbarAlert.open}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          color={snackbarAlert.severity}
          message={snackbarAlert.message}
          autoHideDuration={snackbarAlert.autoHideDuration}
        />
      </Box>
    </Box>
  );
};

export default ViewClientModel;
